import { ReactNode } from "react";

// Types and Interfaces
export interface SubscriptionMetadata {
  businessName: string;
  phone: string;
  email: string;
}
export interface subscriptions {
  status: string;
  startDate?: string;
  endDate?: string;
  plan: string;
  costPerMonth?: number;
  usage: {
    expenses: number;
    invoices: number;
    inventories: number;
    services: number;
    expenseAI: number;
    users: number;
    invoicePaymentUpload: number;
  };
  limits: {
    expenses: number;
    invoices: number;
    inventories: number;
    services: number;
    expenseAI: number;
    users: number;
    invoicePaymentUpload: number;
  };
  daysTotal: number;
  daysUsed: number;
  companyId: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  "companyId#status": string;
  paymentStatus: string;
  expenseLimit: number;
  expenseUsage: number;
  invoiceUsage: number;
  invoiceLimit: number;
  expenseAIUsage: number;
  usersUsage: number;
  invoicePaymentUploadUsage: number;
  expenseAILimit: number;
  usersLimit: number;
  invoicePaymentUploadLimit: number;
}
export interface SubscriptionFeatures {
  hasExpenseAI: boolean;
  hasInvoicePaymentUpload: boolean;
  support: boolean;
  hasUser: boolean;
}

export interface SubscriptionLimits {
  invoices: number;
  inventories: number;
  services: number;
  expenses: number;
  expenseAI: number;
  users: number;
  invoicePaymentUpload: number;
  quotes: number;
  reports: number;
}

export interface SubscriptionUsage {
  invoices: number;
  inventories: number;
  services: number;
  expenses: number;
  expenseAI: number;
  users: number;
  invoicePaymentUpload: number;
  weeklyReports: number;
  quotes: number;
  recurringInvoices: number;
  reports: number;
}

// Modules that are always accessible regardless of plan
export interface UnlimitedModules {
  reports: boolean;
  weeklyReports: boolean;
  recurringInvoices: boolean;
  dashboard: boolean;
  settings: boolean;
  profile: boolean;
  customers: boolean;
}

export interface SubscriptionData {
  metadata: SubscriptionMetadata;
  companyId: string;
  endDate: string;
  status: SubscriptionStatus;
  paymentStatus: PaymentStatus;
  plan: SubscriptionPlan;
  features: SubscriptionFeatures;
  limits: SubscriptionLimits;
  usage: SubscriptionUsage;
  daysUsed: number;
  daysTotal: number;
  id?: string;
  startDate?: string;
  costPerMonth?: number;
  nextDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type ModuleName = keyof SubscriptionLimits | keyof SubscriptionUsage;

export type SubscriptionStatus =
  | "active"
  | "expired"
  | "cancelled"
  | "suspended";
export type PaymentStatus = "completed" | "pending" | "failed" | "cancelled";
export type SubscriptionPlan = "free" | "basic" | "pro" | "enterprise";

export interface ModuleStatus {
  limit: number | typeof Infinity;
  usage: number;
  remaining: number | typeof Infinity;
  isAtLimit: boolean;
  isOverLimit: boolean;
  percentage: number;
}

export interface ModuleCheckResult {
  allowed: boolean;
  reason:
    | "allowed"
    | "subscription_inactive"
    | "feature_not_available"
    | "limit_exceeded";
}

export interface SubscriptionService {
  subscription: SubscriptionData;
  isActive: () => boolean;
  hasSubscription: () => boolean;
  hasFeature: (feature: keyof SubscriptionFeatures) => boolean;
  getModuleStatus: (module: ModuleName) => ModuleStatus;
  canUseModule: (
    module: ModuleName,
    requiredCount?: number,
  ) => ModuleCheckResult;
  getUpgradeMessage: (
    module: ModuleName,
  ) => { reason: string; id: string } | null;
  getDaysRemaining: () => number;
  MODULES: typeof MODULES;
  SUBSCRIPTION_STATUS: typeof SUBSCRIPTION_STATUS;
  SubscriptionGuard: React.FC<SubscriptionGuardProps>;
  refresh: () => void;
  checkValid: boolean;
  triggerSubscriptionModal: (module: ModuleName, reason?: string) => void;
}

// Subscription Guard Component Props
export interface SubscriptionGuardProps {
  module: ModuleName;
  requiredCount?: number;
  children: ReactNode;
  fallback?: ReactNode;
  showUpgradeMessage?: boolean;
  showSecondaryText?: boolean;
  isMobileListPage?: boolean;
  isListPage?: boolean;
  outlined?: boolean;
  isInfo?: boolean;
  createTitle?: string;
  className?: string;
}


// Constants
export const MODULES = {
  INVOICES: "invoices" as const,
  SERVICES: "services" as const,
  EXPENSES: "expenses" as const,
  INVENTORIES: "inventories" as const,
  USERS: "users" as const,
  EXPENSE_AI: "expenseAI" as const,
  INVOICE_PAYMENT_UPLOAD: "invoicePaymentUpload" as const,
  REPORTS: "reports" as const,
  WEEKLY_REPORTS: "weeklyReports" as const,
  RECURRING_INVOICES: "recurringInvoices" as const,
  DASHBOARD: "dashboard" as const,
  SETTINGS: "settings" as const,
  PROFILE: "profile" as const,
};

export const UNLIMITED_MODULES: Record<keyof UnlimitedModules, boolean> = {
  reports: true,
  weeklyReports: true,
  recurringInvoices: true,
  dashboard: true,
  settings: true,
  customers: true,
  profile: true,
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: "active" as const,
  EXPIRED: "expired" as const,
  CANCELLED: "cancelled" as const,
  SUSPENDED: "suspended" as const,
};

export const PAYMENT_STATUS = {
  COMPLETED: "completed" as const,
  PENDING: "pending" as const,
  FAILED: "failed" as const,
  CANCELLED: "cancelled" as const,
};

// Modules that require specific feature flags
export const FEATURE_GATED_MODULES: Record<string, keyof SubscriptionFeatures> = {
  expenseAI: "hasExpenseAI",
  invoicePaymentUpload: "hasInvoicePaymentUpload",
  // users: "hasUser",
};

// Subscription Provider Component Props
export interface SubscriptionProviderProps {
  children: ReactNode;
  subscription?: SubscriptionData;
}
